import {Inject, Injectable} from '@angular/core';
import {ENVIRONMENT} from '../../../const';
import {HttpClient} from '@angular/common/http';
import {BehaviorSubject, map, Observable, of, tap} from 'rxjs';
import {SettingFlag, Settings, SettingValue} from '../models/settings';
import {AppSettings} from "../models/appSettings";

@Injectable({
    providedIn: 'root'
})
export class SettingsService {

    private endpoint: string;

    private appSettings = new BehaviorSubject<AppSettings[] | null>(null);

    public appSettings$ = this.appSettings.asObservable();

    private allSettings$: Observable<AppSettings[]> | null = null;

    constructor(
        @Inject(ENVIRONMENT) protected environment: any,
        private http: HttpClient
    ) {
        this.endpoint = environment.endpoint;
    }

    getPdfFormatSetting(): Observable<SettingFlag> {
        return this.http.get<SettingFlag>(`${this.endpoint}/settings/format/pdf/enabled`);
    }

    getExcelFormatSetting(): Observable<SettingFlag> {
        return this.http.get<SettingFlag>(`${this.endpoint}/settings/format/excel/enabled`);
    }

    getDocFormatSetting(): Observable<SettingFlag> {
        return this.http.get<SettingFlag>(`${this.endpoint}/settings/format/doc/enabled`);
    }

    getMarkdownFormatSetting(): Observable<SettingFlag> {
        return this.http.get<SettingFlag>(`${this.endpoint}/settings/format/md/enabled`);
    }

    getPowerpointFormatSetting(): Observable<SettingFlag> {
        return this.http.get<SettingFlag>(`${this.endpoint}/settings/format/pptx/enabled`);
    }

    getDeletionEnabled(): Observable<SettingFlag> {
        return this.http.get<SettingFlag>(`${this.endpoint}/settings/deletion/enabled`);
    }

    getDocumentLimit(): Observable<SettingValue> {
        return this.http.get<SettingValue>(`${this.endpoint}/settings/dimension/limit`);
    }

    postFormatSetting(params: Settings): Observable<any> {
        return this.http.post(`${this.endpoint}/settings/format`, params);
    }

    postDocumentLimitSetting(params: Settings): Observable<any> {
        return this.http.post(`${this.endpoint}/settings/dimension/limit`, params);
    }

    postDeletionEnabledSetting(params: Settings): Observable<any> {
        return this.http.post(`${this.endpoint}/settings/deletion/enabled`, params);
    }

    loadAllSettings(): Observable<AppSettings[]> {
        if (this.appSettings.value !== null) {
            return of(this.appSettings.value);
        }
        return this.http.get<AppSettings[]>(`${this.endpoint}/settings/app/all`).pipe(
            tap(settings => {
                this.appSettings.next(settings);
            })
        );
    }

    invalidateSettingsCache(): void {
        this.appSettings.next(null);
    }

    isFeatureEnabled(featureName: string, configId?: string): Observable<boolean> {
        return this.loadAllSettings().pipe(
            map(settings => {
                const match = (settings ?? []).find(setting =>
                    setting.featureName?.toLowerCase() === featureName.toLowerCase() &&
                    (configId === undefined || setting.configId?.toLowerCase() === configId.toLowerCase())
                );
                return match?.isEnabled ?? false;
            })
        );
    }
}